<template>
  <div class="register-container">
    <form @submit.prevent="register">
      <h2>用户注册</h2>
      <div class="form-group">
        <label for="username">用户名</label>
        <input 
          type="text" 
          id="username" 
          v-model="user.username" 
          required
          placeholder="请输入用户名"
        />
      </div>
      
      <div class="form-group">
        <label for="password">密码</label>
        <input 
          type="password" 
          id="password" 
          v-model="user.password" 
          required
          placeholder="请输入密码"
        />
      </div>
      
      <div class="form-group">
        <label for="email">邮箱</label>
        <div class="email-input-group">
          <input 
            type="email" 
            id="email" 
            v-model="user.email" 
            required
            placeholder="请输入邮箱"
            @blur="validateEmail"
          />
          <button 
            type="button" 
            class="send-code-btn"
            @click="sendVerificationCode"
            :disabled="!isEmailValid || isSendingCode || countdown > 0 || isSolvingPow"
          >
            {{ getCodeButtonText }}
          </button>
        </div>
        <div v-if="emailError" class="field-error">
          {{ emailError }}
        </div>
      </div>

      <div class="form-group pow-group">
        <label>安全验证</label>
        <div v-if="isSolvingPow" class="pow-status">
          <span class="pow-spinner"></span>
          正在计算工作量证明...
        </div>
        <div v-else-if="powSolved" class="pow-status pow-solved">
          ✓ 安全验证已通过
        </div>
        <div class="pow-tip">发送验证码时将自动完成工作量证明验证</div>
      </div>
      
      <div class="form-group">
        <label for="verification-code">验证码</label>
        <input 
          type="text" 
          id="verification-code" 
          v-model="user.verification_code" 
          required
          placeholder="请输入邮箱验证码"
          maxlength="6"
        />
      </div>
      
      <div class="form-group">
        <button type="submit" :disabled="isLoading || !isFormValid">
          {{ isLoading ? '注册中...' : '注册' }}
        </button>
      </div>
      
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
      
      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>
      
      <div class="login-link">
        已有账号？<router-link to="/login">去登录</router-link>
      </div>
      
      <div class="icp-info">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
          豫ICP备2025139425号-1
        </a>
      </div>
    </form>
  </div>
</template>

<script>
import api from '@/services/api';
import { solvePowChallenge } from '@/utils/pow';

export default {
  name: 'RegisterPage',
  data() {
    return {
      user: {
        username: '',
        password: '',
        email: '',
        verification_code: ''
      },
      isLoading: false,
      isSendingCode: false,
      isSolvingPow: false,
      powSolved: false,
      error: null,
      successMessage: null,
      emailError: null,
      isEmailValid: false,
      countdown: 0,
      countdownTimer: null
    };
  },
  computed: {
    isFormValid() {
      return this.user.username && 
             this.user.password && 
             this.user.email && 
             this.user.verification_code && 
             this.isEmailValid;
    },
    getCodeButtonText() {
      if (this.countdown > 0) {
        return `${this.countdown}秒后重发`;
      }
      return this.isSendingCode ? '发送中...' : '发送验证码';
    }
  },
  methods: {
    validateEmail() {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!this.user.email) {
        this.emailError = null;
        this.isEmailValid = false;
        return;
      }
      
      if (!emailRegex.test(this.user.email)) {
        this.emailError = '邮箱格式不正确';
        this.isEmailValid = false;
      } else {
        this.emailError = null;
        this.isEmailValid = true;
      }
    },
    
    async sendVerificationCode() {
      if (!this.isEmailValid) {
        this.emailError = '请输入正确的邮箱地址';
        return;
      }
      
      this.isSendingCode = true;
      this.isSolvingPow = true;
      this.powSolved = false;
      this.error = null;
      this.successMessage = null;
      
      try {
        const { challenge, nonce } = await solvePowChallenge();
        this.powSolved = true;
        this.isSolvingPow = false;
        
        await api.sendVerificationCode(this.user.email, challenge, nonce);
        this.successMessage = '验证码已发送到您的邮箱，请查收';
        this.startCountdown();
      } catch (err) {
        console.error('发送验证码失败:', err);
        this.powSolved = false;
        this.error = err.response?.data?.message || err.message || '发送验证码失败，请稍后再试';
      } finally {
        this.isSolvingPow = false;
        this.isSendingCode = false;
      }
    },
    
    startCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }

      this.countdown = 60;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
      }, 1000);
    },
    
    async register() {
      if (!this.isFormValid) {
        this.error = '请填写完整信息';
        return;
      }
      
      this.isLoading = true;
      this.error = null;
      this.successMessage = null;
      
      try {
        const response = await api.register(this.user);
        console.log('注册成功:', response.data);
        this.successMessage = '注册成功！正在跳转到登录页面...';
        
        setTimeout(() => {
          this.$router.push('/login');
        }, 2000);
      } catch (err) {
        console.error('注册失败:', err);
        this.error = err.response?.data?.message || '注册失败，请稍后再试';
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
  },
  beforeUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }
};
</script>

<style scoped>
.register-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 20px;
  box-sizing: border-box;
}

.register-container form {
  background: white;
  max-width: 450px;
  width: 100%;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

h2 {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
  font-size: 24px;
  font-weight: 500;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  box-sizing: border-box;
}

input:focus {
  outline: none;
  border-color: #007bff;
}

.email-input-group {
  display: flex;
  gap: 10px;
}

.email-input-group input {
  flex: 1;
}

.send-code-btn {
  padding: 12px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  min-width: 100px;
  transition: background-color 0.3s ease;
}

.send-code-btn:hover:not(:disabled) {
  background: #218838;
}

.send-code-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

button[type="submit"] {
  width: 100%;
  padding: 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

button[type="submit"]:hover:not(:disabled) {
  background: #0056b3;
}

button[type="submit"]:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.error-message {
  color: #dc3545;
  margin-top: 10px;
  padding: 10px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  font-size: 14px;
}

.success-message {
  color: #155724;
  margin-top: 10px;
  padding: 10px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  font-size: 14px;
}

.field-error {
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.pow-group {
  margin-top: -4px;
}

.pow-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f8f9fa;
  font-size: 14px;
  color: #555;
}

.pow-status.pow-solved {
  color: #155724;
  background: #d4edda;
  border-color: #c3e6cb;
}

.pow-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ccc;
  border-top-color: #007bff;
  border-radius: 50%;
  animation: pow-spin 0.6s linear infinite;
}

@keyframes pow-spin {
  to {
    transform: rotate(360deg);
  }
}

.pow-tip {
  margin-top: 8px;
  color: #666;
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
}

.login-link {
  text-align: center;
  margin-top: 15px;
}

.login-link a {
  color: #007bff;
  text-decoration: none;
}

.login-link a:hover {
  text-decoration: underline;
}

.icp-info {
  text-align: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.icp-info a {
  color: #999;
  text-decoration: none;
  font-size: 12px;
  transition: color 0.3s ease;
}

.icp-info a:hover {
  color: #666;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .register-container {
    padding: 20px 15px;
    min-height: 100vh;
    justify-content: center;
  }
  
  .register-container form {
    padding: 40px 25px;
    margin: 0;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }
  
  h2 {
    font-size: 24px;
    margin-bottom: 30px;
  }
  
  input {
    padding: 14px 16px;
    font-size: 16px; /* 防止iOS缩放 */
    border-radius: 8px;
  }
  
  .send-code-btn {
    padding: 14px 16px;
    font-size: 14px;
    min-width: 120px;
  }
  
  button[type="submit"] {
    padding: 14px;
    font-size: 16px;
    border-radius: 8px;
  }
  
  .email-input-group {
    flex-direction: column;
    gap: 12px;
  }
  
  .send-code-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .register-container {
    padding: 15px 10px;
  }
  
  .register-container form {
    padding: 30px 20px;
    max-width: none;
    margin: 0 -5px;
  }
  
  h2 {
    font-size: 22px;
    margin-bottom: 25px;
  }
  
  input {
    padding: 12px 16px;
    font-size: 16px;
  }
  
  .send-code-btn {
    padding: 12px 16px;
    font-size: 14px;
  }
  
  button[type="submit"] {
    padding: 12px;
    font-size: 16px;
  }
  
  .form-group {
    margin-bottom: 18px;
  }
}

/* 触摸设备优化 */
@media (hover: none) and (pointer: coarse) {
  .send-code-btn:hover:not(:disabled) {
    background: #28a745;
  }
  
  .send-code-btn:active:not(:disabled) {
    background: #1e7e34;
    transform: scale(0.98);
  }
  
  button[type="submit"]:hover:not(:disabled) {
    background: #007bff;
  }
  
  button[type="submit"]:active:not(:disabled) {
    background: #0056b3;
    transform: scale(0.98);
  }
  
  input {
    -webkit-appearance: none;
  }
  
  .login-link a:hover {
    text-decoration: none;
  }
  
  .login-link a:active {
    color: #0056b3;
  }
}
</style>